import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const AIChatWidget = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "ai", text: t("chat.greeting") }]);
    }
  }, [open, t, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const answer = (question) => {
    const quick = t("chat.quick");
    const q = question.toLowerCase();
    const match = quick.find((item) => item.q.toLowerCase() === q || q.includes(item.q.toLowerCase().slice(0, 12)));
    return match ? match.a : t("chat.fallback");
  };

  const ask = (question) => {
    if (!question.trim() || typing) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      setMessages((m) => [...m, { role: "ai", text: answer(question) }]);
      setTyping(false);
    }, 900);
  };

  return (
    <>
      <button
        data-testid="ai-chat-fab"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-400 flex items-center justify-center shadow-xl shadow-indigo-500/40 hover:scale-105 transition-transform duration-300"
        aria-label="Business AI chat"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageSquare className="w-6 h-6 text-white" />}
      </button>

      {open && (
        <div data-testid="ai-chat-window" className="fixed bottom-24 right-5 sm:right-6 z-50 w-[calc(100vw-2.5rem)] max-w-sm glass rounded-3xl shadow-2xl shadow-black/60 overflow-hidden animate-fade-up flex flex-col" style={{ height: "480px" }}>
          <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-3 bg-slate-900/80">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-400 flex items-center justify-center">
              <Sparkles className="w-4.5 h-4.5 w-5 h-5 text-white" />
            </span>
            <div>
              <p className="text-sm font-semibold text-white font-heading">{t("chat.title")}</p>
              <p className="text-[11px] text-slate-500">{t("chat.sub")}</p>
            </div>
            <span className="ml-auto flex items-center gap-1.5 text-[10px] text-emerald-400 font-mono2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flow-pulse" /> ONLINE
            </span>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-indigo-500 text-white rounded-br-md" : "bg-slate-800/90 text-slate-200 rounded-bl-md"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-slate-800/90 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                  {[0, 1, 2].map((d) => (
                    <span key={d} className="w-1.5 h-1.5 rounded-full bg-slate-500 flow-pulse" style={{ animationDelay: `${d * 0.25}s` }} />
                  ))}
                </div>
              </div>
            )}
            {messages.length <= 1 && !typing && (
              <div className="flex flex-wrap gap-2 pt-1">
                {t("chat.quick").map((item, i) => (
                  <button
                    key={i}
                    data-testid={`chat-quick-${i}`}
                    onClick={() => ask(item.q)}
                    className="text-xs text-cyan-300 border border-cyan-500/30 hover:border-cyan-400/60 hover:bg-cyan-500/10 rounded-full px-3 py-1.5 transition-colors text-left"
                  >
                    {item.q}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-slate-800 bg-slate-900/80">
            <div className="flex items-center gap-2">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask(input)}
                placeholder={t("chat.placeholder")}
                className="flex-1 bg-slate-800/80 border border-slate-700 focus:border-indigo-500 rounded-full px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 outline-none transition-colors"
              />
              <button
                data-testid="ai-chat-send"
                onClick={() => ask(input)}
                className="w-10 h-10 rounded-full bg-indigo-500 hover:bg-indigo-400 flex items-center justify-center transition-colors"
                aria-label="Send"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
